import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, Tooltip, styled } from '@mui/material';

import { addSeconds, format, isValid } from 'date-fns';

import type { PlanningEntity } from 'components/roster-portal/activity-planning/types';
import { useSelector } from 'hooks';

import { ZoomLevel as DetailZoomLevel } from 'components/roster-portal/activity-planning/components/Header/ZoomLevelMenu.enums';
import type { PlanningEntityIdentifier } from 'generated/index';
import type { ZoomLevel } from 'components/roster-portal/activity-planning/TimelineView/TimelineBar/TimelineBar.enums';

import { BLUE_TERTIARY, PRIMARY } from 'constants/colors';
import { getSecondsPerPixel } from 'components/roster-portal/activity-planning/TimelineView/utils';

const RouteContainer = styled(Box, {
  shouldForwardProp: (p) => !['detailZoomLevel'].includes(p as string)
})<{
  detailZoomLevel: DetailZoomLevel;
}>(({ detailZoomLevel }) => ({
  position: 'relative',
  top: '50%',
  width: '100%',
  height: detailZoomLevel === DetailZoomLevel.COMPACT ? '60px' : '75px',
  padding: '4px',
  boxSizing: 'border-box',
  backgroundColor: 'rgba(0, 0, 0, 0.015)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
  borderRadius: '8px',
  border: `2px solid ${BLUE_TERTIARY}`,
  // Toggled by dropTargetForElements callbacks — no React re-renders.
  '&[data-drag-over]': {
    borderColor: PRIMARY
  }
}));

type Props = {
  planningEntity: PlanningEntity;
  children: React.ReactNode;
  start: Date;
  scheduleStart?: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
  zoomLevel: ZoomLevel;
  timeBarItemWidth: number;
};

// Pragmatic port of `TimelineTooltipContainer`: dropTargetForElements drives
// position via refs + a data-attribute highlight, avoiding hover re-renders.
export default function TimelineTooltipContainerPragmatic({
  children,
  start,
  planningEntity,
  scheduleStart,
  planningEntityIdentifier,
  zoomLevel,
  timeBarItemWidth
}: Props) {
  'use memo';
  const [open, setOpen] = useState(false);
  // Ref so onDrag reads `open` without re-registering the drop target on toggle.
  // Assigned in a layout effect (never during render) so the React Compiler can
  // optimize this component.
  const openRef = useRef(false);
  useLayoutEffect(() => {
    openRef.current = open;
  });

  const defaultDetailZoomLevel = useSelector(
    (state) => state.preferences.defaultZoomLevel
  );
  const detailZoomLevel = defaultDetailZoomLevel ?? DetailZoomLevel.STANDARD;

  const areaRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // x relative to route's left edge — kept in a ref so high-frequency onDrag
  // never triggers a render.
  const positionXRef = useRef<number>(0);
  const screenPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  // Chip is centered on cursor (centerUnderPointer); its left edge is at
  // `cursorX − chipWidth/2`. Tooltip anchors to that left edge.
  const chipWidthRef = useRef<number>(0);

  // Snapshot of the drag position for the render phase, updated only while the
  // tooltip is open (cheap re-render). The refs above hold the latest values for
  // the (non-render) getData / anchorEl callbacks; this state mirrors them for
  // render so the React Compiler can optimize this component.
  const [renderPos, setRenderPos] = useState({
    positionX: 0,
    screenX: 0,
    screenY: 0,
    chipWidth: 0
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updatePosition = (input: { clientX: number; clientY: number }) => {
      const bounds = areaRef.current?.getBoundingClientRect();
      if (!bounds) return;
      positionXRef.current = input.clientX - bounds.left;
      screenPositionRef.current = { x: input.clientX, y: input.clientY };
    };

    // Open tooltip + capture source data; covers both onDragStart (source row)
    // and onDragEnter (entering from another row).
    const openWithSource = (
      input: { clientX: number; clientY: number },
      source: { data?: Record<string, unknown> }
    ) => {
      updatePosition(input);
      chipWidthRef.current =
        (source.data?.chipWidth as number | undefined) ?? 0;
      el.setAttribute('data-drag-over', '');
      setOpen(true);
      setRenderPos({
        positionX: positionXRef.current,
        screenX: screenPositionRef.current.x,
        screenY: screenPositionRef.current.y,
        chipWidth: chipWidthRef.current
      });
    };

    const cleanup = dropTargetForElements({
      element: el,
      getData: () => ({
        position: { x: positionXRef.current, y: 0 },
        planningEntityId: planningEntity.planningEntityIdentifier?.entityId,
        scheduleStart,
        planningEntityIdentifier,
        isToPlan: false
      }),
      onDragStart: ({ location, source }) => {
        openWithSource(location.current.input, source);
      },
      onDragEnter: ({ location, source }) => {
        openWithSource(location.current.input, source);
      },
      onDrag: ({ location, source }) => {
        updatePosition(location.current.input);
        chipWidthRef.current =
          (source.data?.chipWidth as number | undefined) ?? 0;
        // Only update while open — keeps `open` out of this effect's deps.
        if (openRef.current)
          setRenderPos({
            positionX: positionXRef.current,
            screenX: screenPositionRef.current.x,
            screenY: screenPositionRef.current.y,
            chipWidth: chipWidthRef.current
          });
      },
      onDragLeave: () => {
        el.removeAttribute('data-drag-over');
        setOpen(false);
      },
      onDrop: () => {
        el.removeAttribute('data-drag-over');
        setOpen(false);
      }
    });

    return () => {
      cleanup();
      el.removeAttribute('data-drag-over');
    };
  }, [
    planningEntity.planningEntityIdentifier?.entityId,
    scheduleStart,
    planningEntityIdentifier
    // `open` intentionally omitted — closure reads `openRef.current` instead.
  ]);

  const secondsPerPixel = getSecondsPerPixel(timeBarItemWidth, zoomLevel);
  const tooltipTime = (() => {
    if (!isValid(start)) return undefined;
    const chipLeftInRoute = renderPos.positionX - renderPos.chipWidth / 2;
    const newDate = addSeconds(start, chipLeftInRoute * secondsPerPixel);
    if (!isValid(newDate)) return undefined;
    return format(newDate, 'HH:mm');
  })();

  return (
    <Tooltip
      placement="top"
      enterDelay={500}
      open={open}
      title={tooltipTime}
      followCursor
      arrow
      slotProps={{
        popper: {
          anchorEl: {
            getBoundingClientRect: () => {
              if (!areaRef.current) {
                return new DOMRect(0, 0, 0, 0);
              }
              const { y, height } = areaRef.current.getBoundingClientRect();
              // Anchor at chip's top-left (cursorX − chipWidth/2, route midline).
              const chipLeftScreenX =
                screenPositionRef.current.x - chipWidthRef.current / 2;
              return new DOMRect(chipLeftScreenX, y + height / 2, 0, 0);
            }
          },
          style: {
            zIndex: 10,
            left: renderPos.screenX - renderPos.chipWidth / 2,
            top: renderPos.screenY
          }
        }
      }}
    >
      <Box
        ref={containerRef}
        display="flex"
        width="100%"
        sx={{
          userSelect: 'none'
        }}
      >
        <RouteContainer ref={areaRef} detailZoomLevel={detailZoomLevel}>
          {children}
        </RouteContainer>
      </Box>
    </Tooltip>
  );
}
